<template>
  <div class="form-fields">
    <form @submit.prevent="">
      <div class="grid grid-cols-8 col-gap-16">
        <div class="col-span-5">
          <div class="grid grid-cols-6 col-gap-6">
            <div class="col-span-4">
              <VFormInput
                placeholder="Enter Netsuite Name"
                label="Netsuite Name*"
                @update="value => $emit('set', 'netsuite_name', value)"
                :initialValue="initialValues.netsuite_name"
                :error="errors.netsuite_name"
              />
            </div>
            <div class="col-span-2">
              <VFormInput
                placeholder="Enter Netsuite ID"
                label="Netsuite ID*"
                :onlyDigits="true"
                :initialValue="initialValues.netsuite_id"
                :error="errors.netsuite_id"
                @update="value => $emit('set', 'netsuite_id', value)"
              />
            </div>
          </div>

          <div>
            <VFormInput
              placeholder="Enter Netsuite Name"
              label="Display Name ( defaults to Netsuite's name if not provided )"
              @update="value => $emit('set', 'display_name', value)"
              :initialValue="initialValues.display_name"
            />
          </div>

          <div>
            <VFormInput
              placeholder="Enter Zendesk Value"
              label="Zendesk Value"
              @update="value => $emit('set', 'zendesk_value', value)"
              :initialValue="initialValues.zendesk_value"
            />
          </div>

          <div>
            <VFormTextarea
              placeholder="Enter product description"
              label="Description*"
              :initialValue="initialValues.description"
              :error="errors.description"
              @update="value => $emit('set', 'description', value)"
            />
          </div>

          <div>
            <VFormSelect
              placeholder="Select group from list"
              label="Product Group*"
              :options="seriesOptions"
              :initialSelectedOption="initialValues.group_name"
              :error="errors.product_group"
              @onSelect="value => $emit('set', 'product_group', value)"
            />
          </div>

          <div>
            <VFormSelect
              placeholder="Select machine type from list"
              label="Machine Type*"
              :options="machineTypes"
              optionKey="name"
              :initialSelectedOption="initialValues.machine_type"
              :error="errors.machine_type"
              @onSelect="value => $emit('set', 'machine_type', value)"
            />
          </div>

          <div>
            <VFormInput
              placeholder="Enter External Shopify Link"
              label="External Shopify Link"
              @update="value => $emit('set', 'external_shopify_link', value)"
              :initialValue="initialValues.external_shopify_link"
            />
          </div>

          <div>
            <VFormInput
              placeholder="Enter Training Currinculum Id"
              label="Training Currinculum Id"
              @update="value => $emit('set', 'training_curriculum_id', value)"
              :initialValue="initialValues.training_curriculum_id"
            />
          </div>

          <div>
            <VFormDatePicker
              label="Select the date to ask for training"
              :value="initialValues.training_curriculum_date"
              @pick="date => $emit('set', 'training_curriculum_date', date)"
            />
          </div>

          <div class="mb-6 grid grid-cols-6 gap-10">
            <div class="col-span-2">
              <div class="mb-2 text-xs font-semibold">Count Towards Threshold*</div>
              <div
                v-for="(type, index) in countTowardsThresholdTypes"
                :key="`is-embroidery-type-${index}`"
                class="ratio"
              >
                <input
                  class="ratio-input"
                  :id="type.name"
                  type="radio"
                  :value="type.value"
                  v-model="countTowardsThresholdType"
                />
                <label class="ratio-label" :for="type.name"></label>
                <span class="ratio-name">{{ type.name }}</span>
              </div>
            </div>
            <div class="col-span-2">
              <div class="mb-2 text-xs font-semibold">
                Enabled Shop Rewards (enable item to calculate reward)*
              </div>
              <div
                v-for="(type, index) in hasRewardTypes"
                :key="`has-reward-type-${index}`"
                class="ratio"
              >
                <input
                  class="ratio-input"
                  :id="type.name"
                  type="radio"
                  :value="type.value"
                  v-model="hasRewardType"
                />
                <label class="ratio-label" :for="type.name"></label>
                <span class="ratio-name">{{ type.name }}</span>
              </div>
            </div>
            <div class="col-span-2">
              <div class="mb-2 text-xs font-semibold">Needs AddOns Training</div>
              <div
                v-for="(type, index) in hasAddonsTrainingTypes"
                :key="`has-addons-training-type-${index}`"
                class="ratio"
              >
                <input
                  class="ratio-input"
                  :id="type.name"
                  type="radio"
                  :value="type.value"
                  v-model="hasAddonsTrainingType"
                />
                <label class="ratio-label" :for="type.name"></label>
                <span class="ratio-name">{{ type.name }}</span>
              </div>
            </div>
            <div class="col-span-2">
              <div class="mb-2 text-xs font-semibold">Needs Create Zendesk Ticket</div>
              <div
                v-for="(type, index) in needsZendeskTicketsTypes"
                :key="`needs-zendesk-tickets-type-${index}`"
                class="ratio"
              >
                <input
                  class="ratio-input"
                  :id="type.name"
                  type="radio"
                  :value="type.value"
                  v-model="needsZendeskTicketsType"
                />
                <label class="ratio-label" :for="type.name"></label>
                <span class="ratio-name">{{ type.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-span-3">
          <VFormUploadImage
            :initialSrc="initialValues.thumbnail_path"
            @upload="file => $emit('set', 'thumbnail', file)"
          />
          <div>
            <div class="mb-4 font-semibold">Resources</div>
            <div class="w-1/2 h-12 mb-8">
              <VFormUpload
                label="Parts Book*"
                :accept="['application/pdf']"
                @upload="value => $emit('set', 'parts_book', value[0])"
              />
            </div>
            <div class="w-1/2 h-12 mb-6">
              <VFormUpload
                label="Quick Start Guide*"
                :accept="['application/pdf']"
                @upload="value => $emit('set', 'quick_start_guide', value[0])"
              />
            </div>
          </div>
        </div>

        <div class="col-span-8">
          <div class="mb-4 font-semibold">Accessories</div>
          <div class="overflow-auto grid grid-cols-3 gap-2 max-h-48">
            <div
              v-for="(accessory, index) in accessories"
              :key="`accessory-${index}`"
              class="flex items-center"
            >
              <div class="order-2 ml-4 truncate">{{ accessory.name }}</div>
              <div class="checkbox">
                <input
                  class="checkbox-input"
                  type="checkbox"
                  :id="`checkbox-${index}`"
                  v-model="checkedItems"
                  :value="accessory"
                />
                <label class="checkbox-label" :for="`checkbox-${index}`"></label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { safeNullDataset } from '@/utils';

import VFormInput from '@/components/base/VFormInput';
import VFormDatePicker from '@/components/base/VFormDatePicker';
import VFormTextarea from '@/components/base/VFormTextarea';
import VFormUploadImage from '@/components/base/VFormUploadImage';
import VFormSelect from '@/components/base/VFormSelect';
import VFormUpload from '@/components/base/VFormUpload';

const COUNT_TOWARDS_THRESHOLD_TYPES = [
  { name: 'Yes', value: 1 },
  { name: 'No', value: 0 },
];

const HAS_REWARD_TYPES = [
  { name: 'Enabled', value: 1 },
  { name: 'Disabled', value: 0 },
];

const HAS_ADDONS_TRAINING_TYPES = [
  { name: 'Need', value: 1 },
  { name: 'No Need', value: 0 },
];

const NEEDS_ZENDESK_TICKETS_TYPES = [
  { name: 'Create Ticket', value: 1 },
  { name: 'No Create Ticket', value: 0 },
];

export default {
  name: 'ProductForm',
  components: {
    VFormInput,
    VFormTextarea,
    VFormUploadImage,
    VFormSelect,
    VFormUpload,
    VFormDatePicker,
  },
  props: {
    initialValues: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    series: {
      type: Array,
      required: false,
      default: () => [],
    },
    accessories: {
      type: Array,
      required: false,
      default: () => [],
    },
    machineTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    errors: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    seriesOptions() {
      return this.series.map(serie => serie.name);
    },
  },
  data() {
    return {
      checkedItems: [],
      countTowardsThresholdTypes: COUNT_TOWARDS_THRESHOLD_TYPES,
      countTowardsThresholdType: '',
      hasRewardTypes: HAS_REWARD_TYPES,
      hasRewardType: '',
      hasAddonsTrainingTypes: HAS_ADDONS_TRAINING_TYPES,
      hasAddonsTrainingType: '',
      needsZendeskTicketsTypes: NEEDS_ZENDESK_TICKETS_TYPES,
      needsZendeskTicketsType: '',
    };
  },
  watch: {
    checkedItems(accessories) {
      this.$emit('set', 'accessories', accessories);
    },
    countTowardsThresholdType(countTowardsThreshold) {
      this.$emit('set', 'count_towards_threshold', countTowardsThreshold);
    },
    hasRewardType(hasReward) {
      this.$emit('set', 'has_reward', hasReward);
    },
    hasAddonsTrainingType(hasAddonsTraining) {
      this.$emit('set', 'has_addons_training', hasAddonsTraining);
    },
    needsZendeskTicketsType(needsZendeskTickets) {
      this.$emit('set', 'needs_zendesk_tickets', needsZendeskTickets);
    },
  },
  mounted() {
    this.checkedItems = safeNullDataset(this.initialValues)(['accessories', 'data']);
  },
  created() {
    this.hasRewardType = this.initialValues.has_reward ?? 1;
    this.countTowardsThresholdType = this.initialValues.count_towards_threshold ?? 1;
    this.hasAddonsTrainingType = this.initialValues.has_addons_training ?? 1;
    this.needsZendeskTicketsType = this.initialValues.needs_zendesk_tickets ?? 1;
  },
};
</script>
